import type { DomainDashboard } from "@/types";
import { clampPercentage, formatScanRemaining } from "@/utils/formatters";

type Props = {
  phase: "exploring" | "interacting" | "analyzing";
  progress?: DomainDashboard["user_scan_progress"];
};

const COPY = {
  exploring: {
    title: "Mapping your site",
    body: "We’re discovering important pages, then we’ll browse them like a visitor to uncover UX issues.",
  },
  interacting: {
    title: "Browsing your site",
    body: "We’re interacting with your website in the browser to uncover usability issues.",
  },
  analyzing: {
    title: "Evaluating usability",
    body: "Browsing is done. We’re reviewing what we saw and filling in open issues.",
  },
} as const;

export default function DomainDiscoverProgressBanner({ phase, progress = null }: Props) {
  const copy = COPY[phase];
  const determinate = phase !== "exploring" && progress != null;
  const percentComplete = determinate ? clampPercentage(Math.round(progress.percent * 100)) : 0;
  const remainingLabel = determinate ? formatScanRemaining(progress.remaining_seconds) : "Usually about a minute";
  const statusLabel = determinate ? `${percentComplete}% complete` : "Finding pages";

  return (
    <div
      className="mb-6 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 sm:px-5"
      role="status"
      data-scan-phase={phase}
    >
      <div className="flex items-start gap-3">
        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 sm:text-base">{copy.title}</p>
              <p className="mt-1 text-sm text-gray-600">{copy.body}</p>
            </div>
            <p className="shrink-0 text-sm font-semibold text-primary sm:text-right">{remainingLabel}</p>
          </div>
          <div
            className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={determinate ? percentComplete : undefined}
          >
            <div
              className={
                determinate
                  ? "h-full rounded-full bg-primary transition-[width]"
                  : "h-full w-1/3 animate-pulse rounded-full bg-primary"
              }
              style={determinate ? { width: `${percentComplete}%` } : undefined}
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">{statusLabel}</p>
        </div>
      </div>
    </div>
  );
}
